import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import styles from "./TradeRecommendations.module.css";
import TradeRecommendationList from "../../components/tradeRecommendations/TradeRecommendationList/TradeRecommendationList";
import TradeRecommendationForm from "../../components/tradeRecommendations/TradeRecommendationForm/TradeRecommendationForm";
import Button from "../../components/common/Button/Button";
import Modal from "../../components/common/Modal/Modal";
import FullscreenModal from "../../components/common/FullscreenModal/FullscreenModal";
import { useTradeRecommendations } from "../../hooks/useTradeRecommendations";
import { usePermission } from "../../hooks/usePermissions";
import { useRealtimePrices } from "../../hooks/useRealtimePrices";
import { subscribeSymbols, unsubscribeDelayed } from "../../ws/marketSubscriptions";
import { tradeRecommendationService } from "../../services/tradeRecommendationsService";

const TradeRecommendations = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialStatus = queryParams.get("status") || '';

  const {
    tradeRecommendations,
    total,
    selectedTradeRecommendation,
    loading,
    error,
    fetchTradeRecommendations,
    createTradeRecommendation,
    updateTradeRecommendation,
    deleteTradeRecommendation,
    searchTradeRecommendations,
    clearSelectedTradeRecommendation,
    clearError,
    getScriptTypes,
  } = useTradeRecommendations();

  const { hasPermission, permissionsLoaded } = usePermission();
  const canViewAll = hasPermission("VIEW_TRADERECOMMENDATIONS");

  // Realtime prices for live profit calculation
  const { prices: realtimePrices } = useRealtimePrices();

  // All filtered trades for stats (separate from paginated table data)
  const [allFilteredTrades, setAllFilteredTrades] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  // Tab state — start as null until permissions load, then set the correct default
  const [activeTab, setActiveTab] = useState(null);
  const userHasSwitchedTab = useRef(false);

  // Set the correct default tab once permissions are loaded
  useEffect(() => {
    if (permissionsLoaded && !userHasSwitchedTab.current) {
      setActiveTab(canViewAll ? 'all' : 'my');
    }
  }, [permissionsLoaded, canViewAll]);

  const [page, setPage] = useState(1);
  const limit = 10;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState(null);
  const [tradeToEdit, setTradeToEdit] = useState(null);
  const [scriptTypes, setScriptTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Track form fields for progress calculation
  const [formData, setFormData] = useState({
    stockId: "",
    recommendationType: "",
    targetPrice: "",
    stopLoss: "",
    timeframe: "",
  });

  // Inside TradeRecommendations component, near other useState hooks
  const [filters, setFilters] = useState({
    status: initialStatus,
    type: '',
    createdFrom: '',
    createdTo: '',
    exitFrom: '',
    exitTo: '',
    searchTerm: '',
    userId: '',
    trackOnly: '',
  });

  // Build effective filters: for 'my' tab, force userId from localStorage
  const effectiveFilters = useMemo(() => {
    if (activeTab === 'my') {
      return {
        ...filters,
        userId: localStorage.getItem("userId") || '',
      };
    }
    return filters;
  }, [filters, activeTab]);

  const extractDateOnly = (isoDateTime) => {
    if (!isoDateTime) return '';
    try {
      const date = new Date(isoDateTime);
      // Get LOCAL date components (what the user actually selected)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // +1 because getMonth() is 0-indexed
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.warn("Failed to parse date:", isoDateTime);
      return '';
    }
  };

  const extractTimeOnly = (isoDateTime) => {
    if (!isoDateTime) return '';
    const timePart = isoDateTime.split('T')[1];
    return timePart ? timePart.substring(0, 5) : ''; // "18:30"
  };

  // Calculate form completion progress
  const formProgress = useMemo(() => {
    const fields = [
      formData.stockId,
      formData.recommendationType,
      formData.targetPrice,
      formData.stopLoss,
      formData.timeframe,
    ];
    const filledFields = fields.filter(
      (field) => field !== null && field !== undefined && String(field).trim()
    ).length;
    return (filledFields / fields.length) * 100;
  }, [formData]);

  // ✅ Fetch trade recommendations when page changes

  useEffect(() => {
    // Don't fetch until permissions have loaded and activeTab is determined
    if (activeTab === null) return;
    fetchTradeRecommendations(page, limit, effectiveFilters);
  }, [page, limit, effectiveFilters, activeTab, fetchTradeRecommendations]);

  // ✅ Fetch ALL filtered trades for stats (separate call, no pagination)
  useEffect(() => {
    if (activeTab === null) return;
    let cancelled = false;
    const fetchStatsData = async () => {
      setStatsLoading(true);
      try {
        const result = await tradeRecommendationService.getAllTradeRecommendations(10000, 0, effectiveFilters);
        if (!cancelled) {
          const tradesData = Array.isArray(result) ? result : result?.data || [];
          setAllFilteredTrades(tradesData);
        }
      } catch (err) {
        console.error("Error fetching stats data:", err);
        if (!cancelled) setAllFilteredTrades([]);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    fetchStatsData();
    return () => { cancelled = true; };
  }, [effectiveFilters, activeTab]);

  // ✅ Subscribe to realtime prices for live profit calculation
  const scriptsToSubscribe = useMemo(() => {
    return [...new Set(
      allFilteredTrades
        .map(trade => trade.script || trade.script_id)
        .filter(Boolean)
        .map(s => s.toUpperCase().trim())
    )];
  }, [allFilteredTrades]);

  useEffect(() => {
    if (scriptsToSubscribe.length === 0) return;
    const page = "TradeRecommendations";
    const context = "TradeStats";
    subscribeSymbols(scriptsToSubscribe, page, context);
    return () => {
      unsubscribeDelayed(scriptsToSubscribe, page, context);
    };
  }, [scriptsToSubscribe]);

  // ✅ Compute trade stats from all filtered trades (same logic as Homepage)
  const tradeStats = useMemo(() => {
    let totalRecommendations = 0;
    let liveCalls = 0;
    let stoplossHit = 0;
    let targetHit = 0;
    let liveProfit = 0;
    let potentialProfit = 0;
    let targetHitProfit = 0;
    let stoplossHitLoss = 0;

    allFilteredTrades.forEach(trade => {
      totalRecommendations++;

      const tradeStatus = trade.status || trade.recommendationstatus;
      const isLive = String(tradeStatus).toLowerCase() === "live";

      if (isLive) liveCalls++;
      if (String(tradeStatus).toLowerCase() === "stoploss hit") stoplossHit++;
      if (String(tradeStatus).toLowerCase() === "target hit") targetHit++;

      const tradeRecommendationId = Number(trade.tradeRecommendationId || trade.traderecommendationid);
      const recoPriceLow = Number(trade.recoPriceLow || trade.recopricelow || 0);
      const entryPrice = Number(trade.entryPrice || trade.entryprice || 0);
      const basePrice = recoPriceLow > 0 ? recoPriceLow : entryPrice;

      const targetOne = Number(trade.targetOne || trade.targetone || 0);
      const target = Number(trade.targetPrice || trade.targetprice || 0);
      const targetPrice = targetOne > 0 ? targetOne : target;

      if (tradeRecommendationId === 1 && targetPrice > 0 && basePrice > 0) {
        potentialProfit += (targetPrice - basePrice);
      } else if (tradeRecommendationId === 2 && targetPrice > 0 && basePrice > 0) {
        potentialProfit += (basePrice - targetPrice);
      }

      const symbolKey = (trade.script || trade.script_id || trade.stocksymbol || trade.stockSymbol || "").toUpperCase().trim();
      const rt = realtimePrices?.[trade.token] || realtimePrices?.[String(trade.token)] || realtimePrices?.[symbolKey];
      let ltp = isLive ? (rt?.price ?? Number(trade.value || trade.ltp || basePrice)) : Number(trade.value || trade.ltp || basePrice);
      const exitPriceLow = Number(trade.exitPriceLow || trade.exitpricelow || 0);

      let actual_profit = 0;
      if (isLive) {
        if (tradeRecommendationId === 1) {
          actual_profit = ltp - basePrice;
        } else if (tradeRecommendationId === 2) {
          actual_profit = basePrice - ltp;
        }
        liveProfit += (actual_profit || 0);
      } else {
        let closedProfit = 0;
        if (tradeRecommendationId === 1 && exitPriceLow > 0 && basePrice > 0) {
          closedProfit = exitPriceLow - basePrice;
        } else if (tradeRecommendationId === 2 && exitPriceLow > 0 && basePrice > 0) {
          closedProfit = basePrice - exitPriceLow;
        }

        if (String(tradeStatus).toLowerCase() === "target hit") {
          targetHitProfit += closedProfit;
        } else if (String(tradeStatus).toLowerCase() === "stoploss hit") {
          stoplossHitLoss += closedProfit;
        }
      }
    });

    return { totalRecommendations, liveCalls, stoplossHit, targetHit, liveProfit, potentialProfit, targetHitProfit, stoplossHitLoss };
  }, [allFilteredTrades, realtimePrices]);

  // Handle form data changes from TradeRecommendationForm
  const handleFormDataChange = useCallback((newFormData) => {
    setFormData(newFormData);
  }, []);

  useEffect(() => {
    const fetchScriptTypes = async () => {
      try {
        // Replace with your actual API call
        const types = await getScriptTypes();
        setScriptTypes(types);
      } catch (err) {
        console.error("Failed to fetch script types:", err);
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchScriptTypes();
  }, []);

  const handleCreateTradeRecommendation = async (tradeData) => {
    try {
      await createTradeRecommendation(tradeData);
      setShowCreateModal(false);
      setFormData({
        stockId: "",
        recommendationType: "",
        targetPrice: "",
        stopLoss: "",
        timeframe: "",
      });
    } catch (err) {
      console.error("Failed to create trade recommendation:", err);
    }
  };

  const handleEditTradeRecommendation = (trade) => {
    const tradeWithParsedData = {
      ...trade,

      // Fix date fields (convert ISO datetime → YYYY-MM-DD)
      recoDate: extractDateOnly(trade.recoDate),
      recoExitDate: extractDateOnly(trade.recoExitDate),
      exitDate: extractDateOnly(trade.exitDate),

      // Fix time fields (convert ISO datetime → HH:mm)
      recoTime: trade.recoTime || extractTimeOnly(trade.recoDate),
      exitTime: trade.exitTime || extractTimeOnly(trade.exitDate),

    };

    setTradeToEdit(tradeWithParsedData);
    setFormData({
      stockId: tradeWithParsedData.stockId,
      recommendationType: tradeWithParsedData.recommendationType,
      targetPrice: tradeWithParsedData.targetPrice,
      stopLoss: tradeWithParsedData.stopLoss,
      timeframe: tradeWithParsedData.timeframe,
    });
    setShowEditModal(true);
  };

  const handleUpdateTradeRecommendation = async (tradeData) => {
    if (!tradeToEdit) return;

    try {
      await updateTradeRecommendation(
        tradeToEdit?.tradeId,
        tradeData
      );
      setShowEditModal(false);
      setTradeToEdit(null);
      setFormData({
        stockId: "",
        recommendationType: "",
        targetPrice: "",
        stopLoss: "",
        timeframe: "",
      });
    } catch (err) {
      console.error("Failed to update trade recommendation:", err);
    }
  };

  const handleDeleteTradeRecommendation = (trade) => {
    setTradeToDelete(trade);
    setShowDeleteModal(true);
  };

  const confirmDeleteTradeRecommendation = async () => {
    if (tradeToDelete) {
      try {
        await deleteTradeRecommendation(tradeToDelete?.tradeId);
        // ✅ Pass full filter object
        fetchTradeRecommendations(page, limit, effectiveFilters);
        setShowDeleteModal(false);
        setTradeToDelete(null);
      } catch (err) {
        console.error("Failed to delete trade recommendation:", err);
      }
    }
  };

  const handleViewTradeRecommendation = (trade) => {
    // You could implement a trade detail modal or navigation here
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1); // reset to first page on filter change
  };

  const handleRefresh = () => {
    fetchTradeRecommendations(page, limit, effectiveFilters);
  };

  // Tab switching handler — marks that user explicitly chose a tab
  const handleTabChange = (tab) => {
    userHasSwitchedTab.current = true;
    setActiveTab(tab);
    setPage(1);
    // Reset filters when switching tabs
    setFilters({
      status: '',
      type: '',
      createdFrom: '',
      createdTo: '',
      exitFrom: '',
      exitTo: '',
      searchTerm: '',
      userId: '',
      trackOnly: '',
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setTradeToEdit(null);
    setFormData({
      stockId: "",
      recommendationType: "",
      targetPrice: "",
      stopLoss: "",
      timeframe: "",
    });
    clearSelectedTradeRecommendation();
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      stockId: "",
      recommendationType: "",
      targetPrice: "",
      stopLoss: "",
      timeframe: "",
    });
  };

  const isDirty = false; // This will be handled by the form itself

  // Modal titles and subtitles
  const createModalTitle = "Create New Trade Recommendation";
  const createModalSubtitle =
    "Provide trade details, analysis, and recommendations for stock trading opportunities";
  const editModalTitle = "Edit Trade Recommendation";
  const editModalSubtitle = tradeToEdit
    ? `Editing recommendation for ${tradeToEdit.stockSymbol || tradeToEdit.stocksymbol || "Stock"
    }`
    : "Update trade recommendation details and analysis";

  // Helper function to get recommendation status
  const getRecommendationStatus = (trade) => {
    if (!trade) return "Unknown";

    const status = trade.status || trade.recommendationstatus;
    return status || "Active";
  };

  return (
    <div className={styles.tradeRecommendationsPage}>

      {/* Page Header — Title, Count & Add Button */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageTitle}>Trade Recommendations</h1>
          <p className={styles.pageCount}>{total} recommendation{total !== 1 ? 's' : ''} found</p>
        </div>
        {hasPermission("CREATE_TRADERECOMMENDATIONS") && (
          <div className={styles.pageHeaderRight}>
            <button
              className={styles.btnAddTop}
              onClick={() => setShowCreateModal(true)}
            >
              + Add Recommendation
            </button>
          </div>
        )}
      </div>
{/* Tab Navigation */}
      <div className={styles.tabBar}>
        {canViewAll && (
          <button
            className={`${styles.tabBtn} ${activeTab === 'all' ? styles.tabBtnActive : ''}`}
            onClick={() => handleTabChange('all')}
          >
            <span className={`material-symbols-outlined ${styles.tabIcon}`}>lists</span>
            All Recommendations
          </button>
        )}
        <button
          className={`${styles.tabBtn} ${activeTab === 'my' ? styles.tabBtnActive : ''}`}
          onClick={() => handleTabChange('my')}
        >
          <span className={`material-symbols-outlined ${styles.tabIcon}`}>person</span>
          My Recommendations
        </button>
      </div>
      {/* Trade Overview Stats Strip */}
      {statsLoading && allFilteredTrades.length === 0 ? (
        <div className={styles.statsLoading}>
          <span className={styles.statsLoadingText}>Loading stats...</span>
        </div>
      ) : (
        <div className={styles.statsStrip}>
          {/* Recommendations */}
          <div className={styles.statGroup}>
            <div className={styles.statGroupLabel}>Recommendations</div>
            <div className={styles.statGroupValues}>
              <div className={styles.statItem}>
                <span className={styles.statValue}>{tradeStats.totalRecommendations}</span>
                <span className={styles.statLabel}>Total</span>
              </div>
              <div className={styles.statDivider}></div>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.green}`}>{tradeStats.liveCalls}</span>
                <span className={styles.statLabel}>Live Calls</span>
              </div>
            </div>
          </div>

          {/* Closed Calls */}
          <div className={styles.statGroup}>
            <div className={styles.statGroupLabel}>Closed Calls</div>
            <div className={styles.statGroupValues}>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.green}`}>{tradeStats.targetHit}</span>
                <span className={styles.statLabel}>Target Hit</span>
              </div>
              <div className={styles.statDivider}></div>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.red}`}>{tradeStats.stoplossHit}</span>
                <span className={styles.statLabel}>Stoploss Hit</span>
              </div>
            </div>
          </div>

          {/* Expected Profits */}
          <div className={styles.statGroup}>
            <div className={styles.statGroupLabel}>Expected Profits</div>
            <div className={styles.statGroupValues}>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${tradeStats.liveProfit >= 0 ? styles.green : styles.red}`}>
                  ₹{Math.abs(tradeStats.liveProfit).toFixed(2)}
                </span>
                <span className={styles.statLabel}>Live Profit</span>
              </div>
              <div className={styles.statDivider}></div>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.green}`}>
                  ₹{tradeStats.potentialProfit.toFixed(2)}
                </span>
                <span className={styles.statLabel}>Potential Profit</span>
              </div>
            </div>
          </div>

          {/* Closed P&L */}
          <div className={styles.statGroup}>
            <div className={styles.statGroupLabel}>Closed P&amp;L</div>
            <div className={styles.statGroupValues}>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.green}`}>
                  ₹{tradeStats.targetHitProfit.toFixed(2)}
                </span>
                <span className={styles.statLabel}>Target Hit Profit</span>
              </div>
              <div className={styles.statDivider}></div>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.red}`}>
                  ₹{Math.abs(tradeStats.stoplossHitLoss).toFixed(2)}
                </span>
                <span className={styles.statLabel}>Stoploss Hit Loss</span>
              </div>
            </div>
          </div>
        </div>
      )}

      

      {error && (
        <div className={styles.errorBanner}>
          <span className={styles.errorIcon}>⚠️</span>
          <span className={styles.errorMessage}>{error.message}</span>
          <button
            className={styles.errorClose}
            onClick={clearError}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      <div className={styles.pageContent}>
        <TradeRecommendationList
          tradeRecommendations={tradeRecommendations}
          total={total}
          loading={loading}
          onEdit={handleEditTradeRecommendation}
          onDelete={handleDeleteTradeRecommendation}
          onView={handleViewTradeRecommendation}
          onFilterChange={handleFilterChange}
          currentFilters={filters}
          onRefresh={handleRefresh}
          onAddRecommendation={() => setShowCreateModal(true)}
          scriptTypes={scriptTypes}
          loadingTypes={loadingTypes}
          hideUserIdFilter={activeTab === 'my'}
        />
      </div>

      {/* Create Trade Recommendation Fullscreen Modal */}
      <FullscreenModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        title={createModalTitle}
        subtitle={createModalSubtitle}
        size="fullscreen"
        showProgress={true}
        progress={formProgress}
        isDirty={isDirty}
        loading={loading}
      >
        <TradeRecommendationForm
          onSubmit={handleCreateTradeRecommendation}
          onCancel={handleCloseCreateModal}
          onFormDataChange={handleFormDataChange}
          loading={loading}
          fullscreenMode={true}
        />
      </FullscreenModal>

      {/* Edit Trade Recommendation Fullscreen Modal */}
      <FullscreenModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        title={editModalTitle}
        subtitle={editModalSubtitle}
        size="fullscreen"
        showProgress={true}
        progress={formProgress}
        isDirty={isDirty}
        loading={loading}
      >
        <TradeRecommendationForm
          tradeRecommendation={tradeToEdit}
          onSubmit={handleUpdateTradeRecommendation}
          onCancel={handleCloseEditModal}
          onFormDataChange={handleFormDataChange}
          loading={loading}
          fullscreenMode={true}
        />
      </FullscreenModal>

      {/* Delete Confirmation Modal - Keep as regular modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTradeToDelete(null);
        }}
        title="Confirm Deletion"
        size="small"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setTradeToDelete(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteTradeRecommendation}
              loading={loading}
            >
              Delete Recommendation
            </Button>
          </>
        }
      >
        <div className={styles.deleteConfirmation}>
          <p>
            Are you sure you want to delete this trade recommendation for{" "}
            <strong>
              {tradeToDelete?.stockSymbol ||
                tradeToDelete?.stocksymbol ||
                "this stock"}
            </strong>
            ?
          </p>
        </div>
      </Modal>

      {/* Pagination — matches BlockKeyword style */}
      <div className={styles.bkTableFooter}>
        <span>Showing {tradeRecommendations.length} of {total} records</span>

        <div className={styles.bkPagination}>
          <button
            type="button"
            className={styles.bkPageBtn}
            disabled={page === 1 || loading}
            onClick={() => {
              setPage((prev) => {
                return prev - 1;
              });
            }}
          >
            {"<"}
          </button>

          {(() => {
            const totalPages = Math.ceil(total / limit);
            const pages = [];

            // Always show first page
            if (page > 2) {
              pages.push(
                <button
                  key={1}
                  type="button"
                  className={`${styles.bkPageBtn}`}
                  onClick={() => {
                    setPage(1);
                  }}
                >
                  1
                </button>
              );
              if (page > 3) {
                pages.push(
                  <span key="ellipsis1" className={styles.paginationDots}>
                    ...
                  </span>
                );
              }
            }

            // Show current page and neighbors
            for (let p = Math.max(1, page - 1); p <= Math.min(totalPages, page + 1); p++) {
              pages.push(
                <button
                  key={p}
                  type="button"
                  className={`${styles.bkPageBtn} ${page === p ? styles.active : ''}`}
                  onClick={() => {
                    setPage(p);
                  }}
                >
                  {p}
                </button>
              );
            }

            // Always show last page
            if (page < totalPages - 1) {
              if (page < totalPages - 2) {
                pages.push(
                  <span key="ellipsis2" className={styles.paginationDots}>
                    ...
                  </span>
                );
              }
              pages.push(
                <button
                  key={totalPages}
                  type="button"
                  className={`${styles.bkPageBtn}`}
                  onClick={() => {
                    setPage(totalPages);
                  }}
                >
                  {totalPages}
                </button>
              );
            }

            return pages;
          })()}

          <button
            type="button"
            className={styles.bkPageBtn}
            disabled={page >= Math.ceil(total / limit) || loading}
            onClick={() => {
              setPage((prev) => {
                return prev + 1;
              });
            }}
          >
            {">"}
          </button>
        </div>

        <span className={styles.pageCountInfo}>Page {page} of {Math.ceil(total / limit)}</span>
      </div>
    </div>
  );
};

export default TradeRecommendations;
